import { ref } from '@nuxtjs/composition-api'
import { getLocalized, productService } from '@propeller-commerce/propeller-sdk-v2'
import type {
  AttributeFilter,
  ProductSearchInput,
  ProductTextFilterInput,
  ProductsResponse
} from '@propeller-commerce/propeller-sdk-v2'
import { getPropellerClient, propellerConfig } from './propeller/client'
import { productFilters } from '~/constants/products.module'
import { DEFAULT_SORT, constructSort } from '~/utils/productSort'

// Product grid + facets via the SDK `productService.getProducts`
// (was a hand-rolled `category(slug).products` query). The catalogue IS the
// examenbundel store, so we search the whole catalogue (status active) rather
// than scoping to a category slug. Sidebar facets come from the response
// `filters` block; only the storefront's own dimensions are exposed.

// Attribute name -> the query-string key the sidebar and URLs use for it
// (`EXAMEN_SCHOOLVAK` -> `text:examen_schoolvak`). Derived from the filter
// config so the facet ids we hand to ProductsFilters are exactly the keys it
// reads back, prefix included — the two differ (`list:` vs `text:`) and must
// not be reconstructed by hand.
const FACET_KEY_BY_ATTRIBUTE: Record<string, string> = Object.fromEntries(
  Object.values(productFilters).map(key => [key.split(':').pop()!.toUpperCase(), key])
)

const ACTIVE_STATUS = 'A'

export interface FacetQuery {
  categorySlug?: string
  filters?: Record<string, string | string[]>
  sort?: string
  page?: number
  offset?: number
  hasBundle?: boolean
  term?: string
}

export interface FacetFilterOption {
  type: 'string'
  id: string
  attrName: string
  value: string
  selected: boolean
  count: number
}

export interface FacetFilter {
  id: string
  label?: string
  count: number
  type: string
  attrName: string
  selected: boolean
  options: FacetFilterOption[]
}

interface FacetResult {
  data: {
    items: unknown[]
    availableFilters: FacetFilter[]
    pagination: {
      currentPage: number
      totalPages: number
    }
  }
  input: FacetQuery
}

// ─── v2 input conversion ─────────────────────────────────────────────────────
// Filters are keyed `{TYPE}:{ATTRIBUTE}` (e.g. `text:examen_schoolvak`); the
// attribute name (uppercased) becomes a ProductTextFilterInput.
//
// Matching is exact, and multi-value attributes on the v2 tenant are stored
// comma-split WITHOUT trimming: a product tagged "vmbo-gl, vmbo-tl" yields the
// values `vmbo-gl` and `' vmbo-tl'`. Filtering on `vmbo-tl` therefore returns 0
// while `' vmbo-tl'` returns 45. Every value is sent in both spellings so the
// padded entries match too — `values` is an OR, so the extra spelling is free
// when it does not exist. Reported to Propeller as a data-cleaning item; this
// stays harmless once they trim the values.
function whitespaceVariants (value: string): string[] {
  const trimmed = value.trim()
  return [...new Set([trimmed, ` ${trimmed}`, `${trimmed} `])]
}

function constructTextFilters (inputFilters: Record<string, string | string[]>): ProductTextFilterInput[] {
  return Object.keys(inputFilters).map((key) => {
    const parts = key.split(':')
    const name = (parts.length === 2 ? parts[1] : key).toUpperCase()
    const raw = inputFilters[key]
    const values = Array.isArray(raw) ? raw : [raw]
    return {
      name,
      values: values.flatMap(whitespaceVariants),
      exclude: false
    }
  })
}

// ─── Facet grouping ──────────────────────────────────────────────────────────
// Maps the v2 `filters` block to the `{ id: 'text:examen_schoolvak', options }`
// shape ProductsFilters consumes. Only EXPOSED_FACETS are surfaced.
//
// Untrimmed values (see `whitespaceVariants`) arrive as separate facet entries —
// `vmbo-gl` (36) next to `' vmbo-gl'` (9) — which rendered as two identical-
// looking checkboxes each covering part of the catalogue. Collapse them on the
// trimmed value and add up the counts.
function mergeWhitespaceDuplicates (options: FacetFilterOption[]): FacetFilterOption[] {
  const byValue = new Map<string, FacetFilterOption>()
  for (const option of options) {
    const value = option.value.trim()
    if (!value) { continue }
    const existing = byValue.get(value)
    if (existing) {
      existing.count += option.count
      existing.selected = existing.selected || option.selected
      continue
    }
    byValue.set(value, { ...option, id: value, attrName: value, value })
  }
  return [...byValue.values()]
}

function groupAvailableFilters (filters: AttributeFilter[]): FacetFilter[] {
  return filters
    .filter((filter) => {
      const name = filter.attributeDescription?.name?.toUpperCase()
      return !!name && !!FACET_KEY_BY_ATTRIBUTE[name] && (filter.textFilters?.length ?? 0) > 0
    })
    .map((filter) => {
      const name = filter.attributeDescription?.name ?? ''
      return {
        id: FACET_KEY_BY_ATTRIBUTE[name.toUpperCase()],
        label: getLocalized(filter.attributeDescription?.descriptions ?? [], 'NL') || name,
        count: 1,
        type: 'text',
        attrName: 'filterKey',
        selected: false,
        options: mergeWhitespaceDuplicates((filter.textFilters ?? []).map(({ value, count, isSelected }) => ({
          type: 'string' as const,
          id: value,
          attrName: value,
          value,
          selected: !!isSelected,
          count
        })))
      }
    })
    .filter(facet => facet.options.length > 0)
}

// ─── Attribute enrichment ────────────────────────────────────────────────────
// The SDK's `getProducts` operation is a fixed generated document that requests
// neither `attributes` nor `shortNames`, and takes no parameter to add them. The
// product card renders producttype and schoolniveau from attributes, so on the
// grid both lines came out blank and the GTM `view_item_list` payload shipped
// empty values. Fetching them per product would be one request per card, so
// instead one extra `products` call re-queries the page's ids for just those two
// fields and the result is merged onto the items.
//
// `language` belongs INSIDE `input` here — `products` has no top-level language
// argument (unlike `product`).
const GRID_ATTRIBUTES_QUERY = `
  query GridProductAttributes($input: ProductSearchInput!, $attributeResultSearchInput: AttributeResultSearchInput) {
    products(input: $input) {
      items {
        ... on Product {
          productId
          shortNames { language value }
          attributes(input: $attributeResultSearchInput) {
            items {
              attributeDescription { name descriptions { language value } }
              value {
                ... on AttributeTextValue { textValues { values } }
                ... on AttributeIntValue { intValue }
                ... on AttributeDecimalValue { decimalValue }
                ... on AttributeEnumValue { enumValues }
              }
            }
          }
        }
      }
    }
  }
`

interface EnrichedProduct {
  productId?: number
  shortNames?: unknown
  attributes?: unknown
}

/**
 * Re-query the grid page's products for `attributes` + `shortNames` and merge
 * them onto the items. Never throws: the grid stays usable (minus the type and
 * level lines) if this call fails.
 */
async function withAttributes (items: unknown[], language: string): Promise<unknown[]> {
  const ids = items
    .map(item => (item as EnrichedProduct)?.productId)
    .filter((id): id is number => typeof id === 'number')

  if (!ids.length) { return items }

  try {
    const data = await getPropellerClient().query(GRID_ATTRIBUTES_QUERY, {
      input: { productIds: ids, statuses: [ACTIVE_STATUS], language, offset: ids.length, page: 1 },
      // `offset` acts as the page size here; the default page returns ~12 of the
      // 79 attributes, which is what truncated the PDP specs in EXAMS-138.
      attributeResultSearchInput: { offset: 500, page: 1 }
    }, 'GridProductAttributes') as { products?: { items?: EnrichedProduct[] } }

    const byId = new Map<number, EnrichedProduct>()
    for (const entry of data?.products?.items ?? []) {
      if (typeof entry?.productId === 'number') { byId.set(entry.productId, entry) }
    }
    if (!byId.size) { return items }

    return items.map((item) => {
      const extra = byId.get((item as EnrichedProduct)?.productId as number)
      return extra ? { ...(item as object), attributes: extra.attributes, shortNames: extra.shortNames } : item
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Propeller grid attribute enrichment failed:', err)
    return items
  }
}

// ─── Normaliser ──────────────────────────────────────────────────────────────
function normalizeResult (response: ProductsResponse, inputQuery: FacetQuery): FacetResult {
  return {
    data: {
      items: response?.items ?? [],
      availableFilters: groupAvailableFilters(response?.filters ?? []),
      pagination: {
        currentPage: response?.page ?? 1,
        totalPages: response?.pages ?? 1
      }
    },
    input: inputQuery
  }
}

// ─── Composable ──────────────────────────────────────────────────────────────
/**
 * Returns `{ result, loading, search }` with `result.value` shaped exactly as
 * ProductGrid consumes:
 *   result.value.data.items            — product list
 *   result.value.data.availableFilters — sidebar filters (grouped)
 *   result.value.data.pagination       — { currentPage, totalPages }
 *   result.value.input                 — the query that produced this result
 */
export function usePropellerFacet ({ language = propellerConfig.language } = {}) {
  const result = ref<FacetResult | null>(null)
  const loading = ref(false)

  const search = async (query: FacetQuery) => {
    loading.value = true
    try {
      // Fall back to the default whenever the requested sort is absent OR not
      // one this endpoint supports (the sidebar-only fetch asks for 'latest'),
      // so the catalogue never falls back to Propeller's implicit order.
      const requestedSort = constructSort(query.sort ?? '')
      const sortInputs = requestedSort.length ? requestedSort : constructSort(DEFAULT_SORT)
      const response = await productService(getPropellerClient()).getProducts({
        language,
        input: {
          language,
          page: query.page ?? 1,
          offset: query.offset ?? 12,
          statuses: [ACTIVE_STATUS],
          // Hidden products are back-office entries, not catalogue items: the
          // v2 tenant keeps "Webshop Examenbundel verzendkosten" (sku A14135,
          // hidden = Y) under the catalogue root, and without this it was
          // listed and filterable as a normal product.
          hidden: false,
          // Scope to the Examenbundel catalog root so we never surface the
          // Thieme/demo root's sample product (productId 1, not orderable).
          categoryId: propellerConfig.catalogRootCategoryId,
          textFilters: constructTextFilters(query.filters ?? {}),
          ...(sortInputs.length ? { sortInputs } : {})
        } as unknown as ProductSearchInput
      })

      const normalized = normalizeResult(response, query)
      normalized.data.items = await withAttributes(normalized.data.items, language)
      result.value = normalized
    } finally {
      loading.value = false
    }
  }

  return { result, loading, search }
}
